import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { api } from '@/lib/api';
import { useApiData } from '@/lib/useApi';
import type { NotificationRow } from '@/lib/types';
import { timeAgo, initials } from '@/lib/format';

interface DashboardHeaderProps {
  title: string;
  collapsed: boolean;
}

export default function DashboardHeader({ title, collapsed }: DashboardHeaderProps) {
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const { data: notifData, reload: reloadNotifs } = useApiData<NotificationRow[]>(() => api.notifications());
  const notifList = notifData ?? [];
  const notifRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  const { profile, logout: signOut } = useAuth();
  const logout = async () => { await signOut(); navigate('/consultancy/login', { replace: true }); };

  const unreadCount = notifList.filter((n) => !n.read_at).length;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markAllRead = async () => {
    try { await api.markAllNotificationsRead(); reloadNotifs(); } catch { /* ignore */ }
  };

  return (
    <header
      className={`fixed top-0 right-0 h-16 bg-white border-b border-[#E1E5EA] flex items-center justify-between px-4 md:px-6 z-30 transition-all duration-300 ${
        collapsed ? 'left-16' : 'left-60'
      }`}
    >
      {/* Title */}
      <h1 className="text-lg font-semibold text-[#000000] tracking-[-0.3px]">{title}</h1>

      {/* Right side */}
      <div className="flex items-center gap-2 md:gap-3">
        {/* Search */}
        <div className="hidden md:flex items-center gap-2 bg-[#F8F9FB] border border-[#E1E5EA] rounded-[10px] px-3 py-1.5 w-48 lg:w-64">
          <div className="w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-search-line text-sm" />
          </div>
          <input
            type="text"
            placeholder="Search..."
            className="bg-transparent text-sm text-gray-600 placeholder-gray-400 outline-none w-full"
          />
        </div>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => { setNotifOpen(!notifOpen); setUserOpen(false); }}
            className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-notification-3-line text-lg text-gray-600" />
            </div>
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 top-11 w-80 bg-white border border-[#E1E5EA] rounded-xl shadow-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#E1E5EA]">
                <span className="text-sm font-semibold text-[#3C3C3C]">Notifications</span>
                <button onClick={markAllRead} className="text-xs text-[#1A73E8] hover:underline cursor-pointer">Mark all read</button>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {notifList.length === 0 && (
                  <div className="px-4 py-8 text-center text-xs text-gray-400">No notifications</div>
                )}
                {notifList.map((n) => (
                  <div
                    key={n.id}
                    onClick={async () => { if (!n.read_at) { try { await api.markNotificationRead(n.id); reloadNotifs(); } catch { /* ignore */ } } }}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer ${!n.read_at ? 'bg-amber-50/40' : ''}`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 text-amber-600 flex-shrink-0 mt-0.5 text-[10px] font-bold">
                      {initials(n.title)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs leading-relaxed ${!n.read_at ? 'text-gray-800 font-medium' : 'text-gray-600'}`}>{n.title}</p>
                      {n.body && <p className="text-[11px] text-gray-500 mt-0.5">{n.body}</p>}
                      <p className="text-[10px] text-gray-400 mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read_at && <div className="w-2 h-2 bg-amber-500 rounded-full mt-1.5 flex-shrink-0" />}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* User Avatar */}
        <div className="relative" ref={userRef}>
          <button
            onClick={() => { setUserOpen(!userOpen); setNotifOpen(false); }}
            className="flex items-center gap-2 cursor-pointer"
          >
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#0B6E77] text-white font-bold text-sm">
              GC
            </div>
            <div className="hidden md:block text-left">
              <p className="text-xs font-semibold text-[#3C3C3C] leading-tight">{profile?.consultancyName}</p>
              <p className="text-[10px] text-gray-400">{profile?.role}</p>
            </div>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-down-s-line text-sm" />
            </div>
          </button>

          {userOpen && (
            <div className="absolute right-0 top-11 w-44 bg-white border border-[#E1E5EA] rounded-xl shadow-lg overflow-hidden">
              <button
                onClick={() => { setUserOpen(false); navigate('/dashboard/settings'); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line text-sm" /></div>
                Profile
              </button>
              <button
                onClick={() => { setUserOpen(false); navigate('/dashboard/settings'); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-settings-3-line text-sm" /></div>
                Settings
              </button>
              <div className="border-t border-[#E1E5EA]" />
              <button
                onClick={logout}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-logout-box-r-line text-sm" /></div>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
